/**
 * SchoolMapReport — Container component with multi-view tabs
 *
 * Renders a header with 5 tabs (Map, Dashboard, Table, Comparison, Analysis),
 * a shared FilterSidebar, and the active view component.
 * The map is kept mounted but hidden when switching to other tabs
 * to avoid Leaflet re-initialization issues.
 */
import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Chip,
  CircularProgress,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Map as MapIcon,
  Dashboard as DashboardIcon,
  TableChart as TableIcon,
  CompareArrows as CompareIcon,
  Analytics as AnalyticsIcon,
  School as SchoolIcon,
  People as PeopleIcon,
  Person as PersonIcon,
  LocationOn as LocationIcon,
} from '@mui/icons-material';

import { ViewTab } from './types';
import { useSchoolData } from './useSchoolData';
import FilterSidebar from './FilterSidebar';
import MapView from './MapView';
import DashboardView from './DashboardView';
import TableView from './TableView';
import ComparisonView from './ComparisonView';
import AnalysisView from './AnalysisView';

import 'leaflet/dist/leaflet.css';

const TAB_CONFIG: { value: ViewTab; label: string; icon: React.ReactElement }[] = [
  { value: 'map', label: 'Map', icon: <MapIcon /> },
  { value: 'dashboard', label: 'Dashboard', icon: <DashboardIcon /> },
  { value: 'table', label: 'Table', icon: <TableIcon /> },
  { value: 'comparison', label: 'Comparison', icon: <CompareIcon /> },
  { value: 'analysis', label: 'Analysis', icon: <AnalyticsIcon /> },
];

const SchoolMapReport: React.FC = () => {
  const [activeView, setActiveView] = useState<ViewTab>('map');
  const [showCoastalZone, setShowCoastalZone] = useState(false);

  const {
    // Filter state
    islandFilter,
    regionFilter,
    districtFilter,
    typeFilters,
    levelFilters,
    choroplethIndicator,
    setChoroplethIndicator,

    // Loading / error
    isLoading,

    // GeoJSON layers
    districtsGeoJSON,
    regionsGeoJSON,

    // Filtered / derived data
    regions,
    districts,
    filteredSchools,
    gpsSchools,
    districtAggregates,
    stats,

    // Proximity & coastal analysis
    nearestNeighborMap,
    coastalDistanceMap,
    coastalZoneGeoJSON,
    coastalStats,
    distanceDistribution,

    // Filter handlers
    handleIslandChange,
    handleRegionChange,
    handleDistrictChange,
    handleTypeToggle,
    handleLevelToggle,
    handleResetFilters,
  } = useSchoolData();

  const handleTabChange = (_: React.SyntheticEvent, newValue: ViewTab) => {
    setActiveView(newValue);
  };

  const handleCoastalZoneToggle = () => {
    setShowCoastalZone((prev) => !prev);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 100px)' }}>
      {/* ── Header ──────────────────────────────────────────────────────── */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 1,
          px: 1,
          flexWrap: 'wrap',
          gap: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <MapIcon color="primary" />
          <Typography variant="h5">School Map Report</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <Chip icon={<SchoolIcon />} label={`${stats.total.toLocaleString()} Schools`} color="primary" />
          <Chip icon={<PeopleIcon />} label={`${stats.totalStudents.toLocaleString()} Students`} color="secondary" />
          <Chip icon={<PersonIcon />} label={`${stats.totalTeachers.toLocaleString()} Teachers`} color="success" />
          <Chip
            icon={<LocationIcon />}
            label={`${stats.withGPS} with GPS`}
            variant="outlined"
            size="small"
          />
        </Box>
      </Box>

      {/* ── Tabs ────────────────────────────────────────────────────────── */}
      <Paper sx={{ mb: 1 }}>
        <Tabs
          value={activeView}
          onChange={handleTabChange}
          variant="fullWidth"
          indicatorColor="primary"
          textColor="primary"
        >
          {TAB_CONFIG.map((tab) => (
            <Tab
              key={tab.value}
              value={tab.value}
              label={tab.label}
              icon={tab.icon}
              iconPosition="start"
              sx={{ minHeight: 48, textTransform: 'none', fontWeight: 600 }}
            />
          ))}
        </Tabs>
      </Paper>

      {/* ── Main content: sidebar + active view ─────────────────────────── */}
      <Box sx={{ display: 'flex', flex: 1, gap: 2, minHeight: 0 }}>
        {/* Left Sidebar — shared across all views */}
        <FilterSidebar
          filters={{
            islandFilter,
            regionFilter,
            districtFilter,
            typeFilters,
            levelFilters,
          }}
          onIslandChange={handleIslandChange}
          onRegionChange={handleRegionChange}
          onDistrictChange={handleDistrictChange}
          onTypeToggle={handleTypeToggle}
          onLevelToggle={handleLevelToggle}
          onResetFilters={handleResetFilters}
          stats={stats}
          regions={regions}
          districts={districts}
          choroplethIndicator={choroplethIndicator}
          onIndicatorChange={setChoroplethIndicator}
          activeView={activeView}
          showCoastalZone={showCoastalZone}
          onCoastalZoneToggle={handleCoastalZoneToggle}
          coastalStats={coastalStats}
        />

        {/* View Area */}
        <Paper sx={{ flex: 1, position: 'relative', overflow: 'hidden' }}>
          {isLoading ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
              }}
            >
              <CircularProgress size={48} />
              <Typography sx={{ mt: 2 }}>Loading schools data...</Typography>
            </Box>
          ) : (
            <>
              {/* Map is kept mounted but hidden to avoid Leaflet re-initialization */}
              <Box sx={{ display: activeView === 'map' ? 'block' : 'none', height: '100%' }}>
                <MapView
                  filteredSchools={filteredSchools}
                  gpsSchools={gpsSchools}
                  districtAggregates={districtAggregates}
                  districtsGeoJSON={districtsGeoJSON}
                  regionsGeoJSON={regionsGeoJSON}
                  choroplethIndicator={choroplethIndicator}
                  islandFilter={islandFilter}
                  regionFilter={regionFilter}
                  districtFilter={districtFilter}
                  typeFilters={typeFilters}
                  levelFilters={levelFilters}
                  coastalZoneGeoJSON={coastalZoneGeoJSON}
                  showCoastalZone={showCoastalZone}
                  nearestNeighborMap={nearestNeighborMap}
                />
              </Box>

              {activeView === 'dashboard' && (
                <Box sx={{ height: '100%', overflow: 'auto', p: 2 }}>
                  <DashboardView
                    filteredSchools={filteredSchools}
                    districtAggregates={districtAggregates}
                    stats={stats}
                  />
                </Box>
              )}

              {activeView === 'table' && (
                <Box sx={{ height: '100%', overflow: 'auto' }}>
                  <TableView
                    filteredSchools={filteredSchools}
                    nearestNeighborMap={nearestNeighborMap}
                    coastalDistanceMap={coastalDistanceMap}
                  />
                </Box>
              )}

              {activeView === 'comparison' && (
                <Box sx={{ height: '100%', overflow: 'auto', p: 2 }}>
                  <ComparisonView
                    filteredSchools={filteredSchools}
                    districtAggregates={districtAggregates}
                    stats={stats}
                  />
                </Box>
              )}

              {activeView === 'analysis' && (
                <Box sx={{ height: '100%', overflow: 'auto', p: 2 }}>
                  <AnalysisView
                    filteredSchools={filteredSchools}
                    gpsSchools={gpsSchools}
                    nearestNeighborMap={nearestNeighborMap}
                    coastalDistanceMap={coastalDistanceMap}
                    coastalStats={coastalStats}
                    distanceDistribution={distanceDistribution}
                    districtAggregates={districtAggregates}
                  />
                </Box>
              )}
            </>
          )}
        </Paper>
      </Box>
    </Box>
  );
};

export default SchoolMapReport;
